import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BottomNavigation } from '../components/BottomNavigation';
import { 
  ArrowLeft,
  Star,
  Heart,
  Message,
  Image as ImageIcon,
  TimeCircle
} from 'react-iconly';
import { BackButton } from '../components/ui';
import { formatTimestamp } from '../lib/dateUtils';

// Style guide colors
const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';
const SUCCESS_GREEN = '#4CAF50';
const WARNING_ORANGE = '#FF9800';
const ERROR_RED = '#F44336';

const headingFont = {
  fontFamily: 'Sora, sans-serif',
  fontWeight: 700,
};
const bodyFont = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
};

// Dummy review data
const dummyReviews = [
  {
    id: 1,
    reviewerName: 'Sarah M.',
    rating: 5,
    body: 'Absolutely delicious! The pasta was perfectly cooked and the carbonara sauce was rich and creamy. The pancetta added the perfect salty crunch. Will definitely order again!',
    timestamp: '2024-01-10T14:30:00Z',
    helpfulCount: 12,
    hasPhoto: true,
    photoUrl: 'https://images.unsplash.com/photo-1621996346565-e3dbc353d2e5?w=400&h=300&fit=crop',
    vendorReply: null
  },
  {
    id: 2,
    reviewerName: 'Mike T.',
    rating: 4,
    body: 'Great flavor and good portion size. The pasta was a bit al dente for my taste, but the sauce was excellent. Delivery was quick and food was still hot.',
    timestamp: '2024-01-09T18:45:00Z',
    helpfulCount: 8,
    hasPhoto: false,
    photoUrl: null,
    vendorReply: {
      text: 'Thank you for the feedback! We\'ll adjust the pasta cooking time to be more tender. We appreciate you trying our carbonara!',
      timestamp: '2024-01-09T19:15:00Z'
    }
  },
  {
    id: 3,
    reviewerName: 'Jennifer L.',
    rating: 5,
    body: 'Best carbonara I\'ve had in the city! The handmade pasta makes all the difference. The cheese was perfectly aged and the black pepper was just right. Highly recommend!',
    timestamp: '2024-01-08T12:20:00Z',
    helpfulCount: 15,
    hasPhoto: true,
    photoUrl: 'https://images.unsplash.com/photo-1476124369491-e7addf5db371?w=400&h=300&fit=crop',
    vendorReply: null
  },
  {
    id: 4,
    reviewerName: 'David K.',
    rating: 3,
    body: 'The taste was okay but the portion was smaller than expected for the price. The pasta was good quality though.',
    timestamp: '2024-01-07T20:10:00Z',
    helpfulCount: 3,
    hasPhoto: false,
    photoUrl: null,
    vendorReply: {
      text: 'We\'re sorry to hear about the portion size concern. Our medium size is designed to be filling, but we\'re happy to offer a larger portion option. Please reach out to us directly for any future orders!',
      timestamp: '2024-01-08T09:30:00Z'
    }
  },
  {
    id: 5,
    reviewerName: 'Emma R.',
    rating: 5,
    body: 'Incredible authentic Italian taste! The pancetta was perfectly crispy and the sauce was creamy without being heavy. The pasta texture was spot on. Can\'t wait to order again!',
    timestamp: '2024-01-06T16:30:00Z',
    helpfulCount: 20,
    hasPhoto: true,
    photoUrl: 'https://images.unsplash.com/photo-1571877227200-a0d98ea607e9?w=400&h=300&fit=crop',
    vendorReply: null
  },
  {
    id: 6,
    reviewerName: 'Alex P.',
    rating: 4,
    body: 'Very good carbonara with authentic flavors. The delivery was a bit slow but the food was still warm. The portion was generous and the quality was excellent.',
    timestamp: '2024-01-05T19:15:00Z',
    helpfulCount: 6,
    hasPhoto: false,
    photoUrl: null,
    vendorReply: null
  }
];

// Calculate rating distribution
const calculateRatingDistribution = (reviews) => {
  const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  reviews.forEach(review => {
    distribution[review.rating]++;
  });
  return distribution;
};

// Calculate average rating
const calculateAverageRating = (reviews) => {
  if (reviews.length === 0) return 0;
  const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
  return (sum / reviews.length).toFixed(1);
};

export function ProductReviewViewer() {
  const { foodId } = useParams();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState(dummyReviews);
  const [activeFilter, setActiveFilter] = useState('all');
  const [foodName, setFoodName] = useState('Handmade Pasta Carbonara');

  // Calculate review statistics
  const averageRating = calculateAverageRating(reviews);
  const ratingDistribution = calculateRatingDistribution(reviews);
  const totalReviews = reviews.length;
  const reviewsWithPhotos = reviews.filter(review => review.hasPhoto).length;

  // Filter reviews based on active filter
  const filteredReviews = reviews.filter(review => {
    switch (activeFilter) {
      case 'photos':
        return review.hasPhoto;
      case 'recent':
        return true; // Already sorted by date
      default:
        return true;
    }
  }).sort((a, b) => {
    if (activeFilter === 'recent') {
      return new Date(b.timestamp) - new Date(a.timestamp);
    }
    return b.helpfulCount - a.helpfulCount; // Sort by helpful count for 'all'
  });

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  const handleHelpfulClick = (reviewId) => {
    setReviews(prevReviews => 
      prevReviews.map(review => 
        review.id === reviewId 
          ? { ...review, helpfulCount: review.helpfulCount + 1 }
          : review
      )
    );
  };

  // Using date-fns formatTimestamp utility

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        set={index < rating ? "bold" : "regular"}
        size={16}
        primaryColor={index < rating ? WARNING_ORANGE : ACCENT_GRAY}
      />
    ));
  };

  const renderRatingBar = (rating, count, total) => {
    const percentage = total > 0 ? (count / total) * 100 : 0;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 14, color: NEUTRAL_TEXT, minWidth: 20, ...bodyFont }}>
          {rating}
        </span>
        <div style={{ 
          flex: 1, 
          height: 8, 
          background: NEUTRAL_BG, 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${percentage}%`,
            height: '100%',
            background: WARNING_ORANGE,
            borderRadius: 4
          }} />
        </div>
        <span style={{ fontSize: 14, color: ACCENT_GRAY, minWidth: 30, ...bodyFont }}>
          {count}
        </span>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: NEUTRAL_BG, paddingBottom: '80px' }}>
      {/* Header */}
      <header style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        zIndex: 100,
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(0,0,0,0.1)'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          padding: '16px 20px',
          gap: 16
        }}>
          <BackButton
            onClick={() => navigate(-1)}
            variant="standard"
            ariaLabel="Back"
          />
          <div style={{ flex: 1 }}>
            <h1 style={{ 
              ...headingFont, 
              fontSize: 18, 
              color: NEUTRAL_TEXT, 
              margin: 0 
            }}>
              Reviews
            </h1>
            <p style={{ 
              fontSize: 14, 
              color: ACCENT_GRAY, 
              margin: 0,
              ...bodyFont
            }}>
              {foodName}
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div style={{ paddingTop: '72px' }}>
        {/* Rating Summary */}
        <div style={{ 
          background: NEUTRAL_WHITE, 
          padding: '24px 20px',
          borderBottom: '1px solid #E0E0E0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                ...headingFont, 
                fontSize: 48, 
                color: NEUTRAL_TEXT,
                lineHeight: 1
              }}>
                {averageRating}
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
                {renderStars(Math.round(parseFloat(averageRating)))}
              </div>
              <div style={{ fontSize: 14, color: ACCENT_GRAY, ...bodyFont }}>
                {totalReviews} reviews
              </div>
            </div>
            <div style={{ flex: 1 }}>
              {[5, 4, 3, 2, 1].map(rating => 
                renderRatingBar(rating, ratingDistribution[rating], totalReviews)
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div style={{ 
          background: NEUTRAL_WHITE, 
          padding: '16px 20px',
          borderBottom: '1px solid #E0E0E0'
        }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { id: 'all', name: 'All', count: totalReviews },
              { id: 'photos', name: 'Photos', count: reviewsWithPhotos },
              { id: 'recent', name: 'Recent', count: totalReviews }
            ].map(filter => (
              <button
                key={filter.id}
                onClick={() => handleFilterChange(filter.id)}
                style={{
                  background: activeFilter === filter.id ? PRIMARY_BROWN : NEUTRAL_BG,
                  color: activeFilter === filter.id ? NEUTRAL_WHITE : NEUTRAL_TEXT,
                  border: 'none',
                  borderRadius: 20,
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  minHeight: 36,
                  ...bodyFont
                }}
              >
                {filter.id === 'photos' && <ImageIcon set="regular" size={16} primaryColor={activeFilter === filter.id ? NEUTRAL_WHITE : NEUTRAL_TEXT} />}
                {filter.id === 'recent' && <TimeCircle set="regular" size={16} primaryColor={activeFilter === filter.id ? NEUTRAL_WHITE : NEUTRAL_TEXT} />}
                {filter.name} ({filter.count})
              </button>
            ))}
          </div>
        </div>

        {/* Reviews List */}
        <div style={{ padding: '20px' }}>
          {filteredReviews.map(review => (
            <div
              key={review.id}
              style={{
                background: NEUTRAL_WHITE,
                borderRadius: 16,
                padding: 20,
                marginBottom: 16,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              {/* Review Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ 
                    ...headingFont, 
                    fontSize: 16, 
                    color: NEUTRAL_TEXT,
                    marginBottom: 4
                  }}>
                    {review.reviewerName}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {renderStars(review.rating)}
                    <span style={{ fontSize: 14, color: ACCENT_GRAY, ...bodyFont }}>
                      {formatTimestamp(review.timestamp)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Review Photo */}
              {review.hasPhoto && review.photoUrl && (
                <div style={{ marginBottom: 16 }}>
                  <img
                    src={review.photoUrl}
                    alt={`Review photo by ${review.reviewerName}`}
                    style={{
                      width: '100%',
                      height: 200,
                      objectFit: 'cover',
                      borderRadius: 12
                    }}
                  />
                </div>
              )}

              {/* Review Text */}
              <p style={{ 
                fontSize: 15, 
                color: NEUTRAL_TEXT, 
                lineHeight: 1.6,
                marginBottom: 16,
                ...bodyFont
              }}>
                {review.body}
              </p>

              {/* Vendor Reply */}
              {review.vendorReply && (
                <div style={{
                  background: NEUTRAL_BG,
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 16,
                  borderLeft: `4px solid ${PRIMARY_BROWN}`
                }}>
                  <div style={{ 
                    ...headingFont, 
                    fontSize: 14, 
                    color: NEUTRAL_TEXT,
                    marginBottom: 8
                  }}>
                    Chef Mario's Kitchen
                  </div>
                  <p style={{ 
                    fontSize: 14, 
                    color: NEUTRAL_TEXT, 
                    lineHeight: 1.5,
                    marginBottom: 8,
                    ...bodyFont
                  }}>
                    {review.vendorReply.text}
                  </p>
                  <span style={{ fontSize: 12, color: ACCENT_GRAY, ...bodyFont }}>
                    {formatTimestamp(review.vendorReply.timestamp)}
                  </span>
                </div>
              )}

              {/* Helpful Button */}
              <button
                onClick={() => handleHelpfulClick(review.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: ACCENT_GRAY,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: 0,
                  ...bodyFont
                }}
              >
                <Heart set="regular" size={16} primaryColor={ACCENT_GRAY} />
                Helpful ({review.helpfulCount})
              </button>
            </div>
          ))}
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
}

export default ProductReviewViewer; 